/**
 * @copyright 2025 Cooltrax Pty Ltd. All rights reserved.
 */

import React, { useState } from 'react'

import { User } from '@auth0/auth0-react'
import { TbUser, TbSettings, TbLogout, TbUserSearch } from 'react-icons/tb'

import { useAuth } from '@/hooks'
import { isGeotabMode } from '@/services/geotabSession'

import { ImpersonateModal } from './ImpersonateModal'
import { ProfileMenu } from './ProfileMenu/ProfileMenu'

export interface ProfileMenuSectionProps {
  handleLogout: () => void
  user: User | undefined
  originalUser?: { firstName: string; lastName: string; email: string } | null
}

export const ProfileMenuSection: React.FC<ProfileMenuSectionProps> = ({ handleLogout, user, originalUser }) => {
  const [showImpersonate, setShowImpersonate] = useState(false)
  const auth = useAuth()

  const handleGoToUrl = (url: string) => {
    if (url === '#logout') {
      handleLogout()
    } else if (url === '#impersonate') {
      setShowImpersonate(true)
    } else {
      // Use window.location.href for proper navigation within the /fresh context
      const fullUrl = window.location.origin + '/fresh' + url
      window.location.href = fullUrl
    }
  }

  const SHOW_PROFILE_SETTINGS = false

  // Server-computed flag: set when the real (pre-impersonation) user is staff,
  // so the control stays available while impersonating a customer.
  const canImpersonate = auth.hasPermission('impersonate')

  // Profile menu options with proper icons
  const profileOptions = [
    ...(SHOW_PROFILE_SETTINGS
      ? [
          {
            key: 'profile',
            label: 'Profile',
            url: '/settings/profile',
            icon: <TbUser className="h-5 w-5 text-primary" />,
          },
          {
            key: 'settings',
            label: 'Settings',
            url: '/settings',
            icon: <TbSettings className="h-5 w-5 text-accent" />,
          },
        ]
      : []),

    ...(canImpersonate
      ? [
          {
            key: 'impersonate',
            label: 'Impersonate',
            url: '#impersonate',
            icon: <TbUserSearch className="h-5 w-5 text-primary" />,
          },
        ]
      : []),

    // No sign-out as a MyGeotab add-in: MyGeotab owns the session, and dropping our token
    // would only strand the user in the frame with no login they could use to get back in.
    ...(isGeotabMode()
      ? []
      : [
          {
            key: 'logout',
            label: 'Sign out',
            url: '#logout',
            icon: <TbLogout className="h-5 w-5 text-error" />,
          },
        ]),
  ]

  return (
    <div data-testid="profile-menu-section">
      <div className="relative z-50 profile-menu-dropdown">
        {/* Using ProfileMenu component from jellyfish-ui with enhanced styling */}
        <ProfileMenu
          user={user}
          originalUser={originalUser}
          options={profileOptions}
          goToUrl={handleGoToUrl}
          className=" rounded-btn rounded-full shadow-md "
        />
      </div>
      {showImpersonate && <ImpersonateModal show={showImpersonate} onHide={() => setShowImpersonate(false)} />}
    </div>
  )
}
